import { FilePicker } from "@/components/FilePicker";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getFontFile, listFonts, uploadFont, type Font, type FontStyle } from "@/api/client";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { fontFaceSupported, useFontFace } from "@/features/fonts/fontFace";
import { TemplateImages } from "@/features/fonts/TemplateImages";
import { ScrollArea } from "@/components/ScrollArea";

const STYLES: { value: FontStyle; label: string }[] = [
  { value: "regular", label: "Regular" },
  { value: "bold", label: "Bold" },
  { value: "italic", label: "Italic" },
  { value: "bolditalic", label: "Bold italic" },
];
const styleLabel = (style: FontStyle) => STYLES.find((s) => s.value === style)?.label ?? style;

const SAMPLE = "Certificate of Membership, Ada Okafor 0123456789";

export function FontLibrary() {
  const fonts = useQuery({ queryKey: ["fonts"], queryFn: listFonts });
  const items = fonts.data ?? [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader title="Fonts and images" detail="TrueType fonts and images that templates can use" />
      <ErrorBanner error={fonts.error} />
      <UploadFontForm />

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <h2 className="border-b border-zinc-200 px-4 py-3 text-sm font-semibold">Fonts</h2>
        {fonts.isLoading ? (
          <EmptyState title="Loading fonts" detail="Fetching uploaded fonts." />
        ) : fonts.isSuccess && items.length === 0 ? (
          <EmptyState title="No fonts yet" detail="Text fields use the built-in Helvetica until a font is uploaded." />
        ) : (
          <ScrollArea label="Fonts">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Family</th>
                  <th className="px-4 py-3 font-medium">Style</th>
                  <th className="px-4 py-3 font-medium">Sample</th>
                  <th className="px-4 py-3 font-medium">Uploaded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((font) => (
                  <tr key={font.id}>
                    <td className="px-4 py-3 font-medium text-zinc-950">{font.family}</td>
                    <td className="px-4 py-3 text-zinc-700">{styleLabel(font.style)}</td>
                    <td className="px-4 py-3">
                      <FontSample font={font} />
                    </td>
                    <td className="px-4 py-3 text-zinc-500">{formatDate(font.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>

      <TemplateImages />
    </section>
  );
}

function FontSample({ font }: { font: Font }) {
  const file = useQuery({
    queryKey: ["font-file", font.id],
    queryFn: () => getFontFile(font.id),
    enabled: fontFaceSupported(),
    staleTime: Infinity,
  });
  // One family name per file, so two styles of the same family never replace each other.
  const family = `cipm-font-${font.id}`;
  const state = useFontFace(family, file.data);

  if (state === "unsupported") return <span className="text-xs text-zinc-500">Sample unavailable in this browser</span>;
  if (state === "failed" || file.isError) return <span className="text-xs text-red-700">This font could not be shown here</span>;
  if (state === "loading") return <span className="text-xs text-zinc-500">Loading font...</span>;
  return (
    <span className="text-lg text-zinc-900" style={{ fontFamily: `"${family}", serif` }}>
      {SAMPLE}
    </span>
  );
}

function UploadFontForm() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [family, setFamily] = useState("");
  const [style, setStyle] = useState<FontStyle>("regular");

  const upload = useMutation({
    mutationFn: (chosen: File) => uploadFont(chosen, family.trim(), style),
    onSuccess: async () => {
      setFile(null);
      setFamily("");
      setStyle("regular");
      await queryClient.invalidateQueries({ queryKey: ["fonts"] });
    },
  });

  const field = "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Upload a font</h2>
      <FilePicker
        label="Font file"
        accept=".ttf,font/ttf"
        extensions={[".ttf"]}
        typeError="Only TrueType (.ttf) fonts can be uploaded"
        maxBytes={10 << 20}
        hint="TrueType (.ttf), up to 10 MB"
        value={file}
        onChange={(next) => {
          setFile(next);
          upload.reset();
        }}
      />
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,0.6fr)_auto] md:items-end">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Family</span>
          <input value={family} onChange={(e) => setFamily(e.target.value)} placeholder="e.g. Garamond" className={field} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Style</span>
          <select value={style} onChange={(e) => setStyle(e.target.value as FontStyle)} className={field}>
            {STYLES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <ToolbarButton disabled={!file || family.trim() === "" || upload.isPending} onClick={() => file && upload.mutate(file)}>
          {upload.isPending ? "Uploading..." : "Upload font"}
        </ToolbarButton>
      </div>
      <p className="text-xs text-zinc-500">
        TrueType (.ttf) only, up to 10 MB; OpenType CFF (.otf) cannot be embedded in PDFs. Upload each style of a family
        separately, under the same family name.
      </p>
      {upload.isSuccess ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Uploaded {upload.data.family} ({styleLabel(upload.data.style)}).
        </p>
      ) : null}
      <ErrorBanner error={upload.error} />
    </div>
  );
}
